/**
 * Carrega um dia REAL no store do roteiro.
 *
 * Substitui a hidratação a partir do dataset de exemplo, que era feita uma vez
 * na abertura do app e valia para qualquer viagem — a razão de uma viagem nova
 * abrir com o roteiro de outra.
 *
 * A ordem é a mesma de antes, e pelo mesmo motivo: **o que está no aparelho
 * vence a rede**. Uma atividade criada offline continua na tela até o outbox
 * conseguir enviá-la; recarregar do servidor por cima apagaria exatamente o
 * trabalho que ainda vai ser sincronizado.
 *
 * O histórico não tem endpoint de leitura ainda. Fica vazio — que é a
 * verdade de uma viagem nova — em vez de vir de exemplo.
 */

import type { Activity } from '@nv/core';
import { useEffect, useState } from 'react';

import { useDiasDaViagem } from '../../trip/dias';
import { useTrip } from '../../trip/TripContext';
import { loadItinerary, localDb, saveItinerary } from './persistence';
import { useItinerary } from './store';

interface AtividadeDoServidor {
  id: string;
  title: string;
  startTime: string;
  locationName: string | null;
  placeId: string | null;
  placeAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  notes: string | null;
  alertMinutesBefore: number | null;
  status: Activity['status'];
  sortOrder: number;
  coverMediaId?: string | null;
  coverThumbUrl?: string | null;
  version: number;
  deletedAt: string | null;
  participantMemberIds?: string[];
}

interface DiaDoServidor {
  day: { id: string; date: string; title: string | null; reorderVersion: number };
  activities: AtividadeDoServidor[];
}

async function lerDia(dayId: string): Promise<DiaDoServidor> {
  const res = await fetch(`/api/v1/days/${dayId}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar o dia (${res.status})`);
  return (await res.json()) as DiaDoServidor;
}

function atividadesDoServidor(resposta: DiaDoServidor): Activity[] {
  return resposta.activities
    .filter((a) => a.deletedAt === null)
    .map((a) => ({
      id: a.id,
      title: a.title,
      startTime: a.startTime,
      status: a.status,
      sortOrder: a.sortOrder,
      ...(a.locationName === null ? {} : { locationName: a.locationName }),
      ...(a.placeId === null ? {} : { placeId: a.placeId }),
      ...(a.placeAddress === null ? {} : { placeAddress: a.placeAddress }),
      ...(a.latitude === null || a.longitude === null ? {} : { latitude: a.latitude, longitude: a.longitude }),
      ...(a.notes === null ? {} : { notes: a.notes }),
      ...(a.alertMinutesBefore === null ? {} : { alertMinutesBefore: a.alertMinutesBefore }),
      ...(a.participantMemberIds && a.participantMemberIds.length > 0 ? { participantMemberIds: a.participantMemberIds } : {}),
      ...(a.coverMediaId ? { coverMediaId: a.coverMediaId, coverThumbUrl: a.coverThumbUrl ?? null } : {}),
      version: a.version,
    })) as Activity[];
}

/**
 * Traz do servidor o que este dia ganhou por fora do aparelho — a atividade
 * que uma enquete virou, por exemplo. O servidor vale por id; o que só existe
 * aqui (criado offline, ainda no outbox) continua na tela.
 */
export async function mesclarDiaDoServidor(dayId: string): Promise<void> {
  const s = useItinerary.getState();
  if (!s.ready || s.dayId !== dayId) return;
  const resposta = await lerDia(dayId);
  // Exclusão ainda na fila: o servidor ainda lista a atividade, mas ela já
  // saiu deste aparelho — trazê-la de volta faria "excluída" virar mentira
  // até a sincronização (teste físico, item 12).
  const excluidasNaFila = new Set(
    (await (await localDb()).outbox.list())
      .filter((m) => m.entity === 'activity' && m.operation === 'delete' && m.status !== 'failed')
      .map((m) => (m.payload as { activityId?: string }).activityId),
  );
  const doServidor = atividadesDoServidor(resposta).filter((a) => !excluidasNaFila.has(a.id));
  const ids = new Set(doServidor.map((a) => a.id));
  const activities = [...doServidor, ...s.activities.filter((a) => !ids.has(a.id))];
  useItinerary.setState({ activities, reorderVersion: resposta.day.reorderVersion });
  if (await loadItinerary(dayId)) {
    await saveItinerary({ activities, history: s.history, currentMemberId: s.currentMemberId, reorderVersion: resposta.day.reorderVersion, dayId });
  }
}

export interface EstadoDoDia {
  /** `null` enquanto os dias da viagem não chegaram. */
  dayId: string | null;
  /** Título e destino que o dia tem no servidor — nunca um exemplo. */
  titulo: string | null;
  carregando: boolean;
  /** A rede falhou E não havia nada no aparelho. */
  erro: boolean;
  /**
   * O que está no store veio do cache do aparelho, não do servidor. O cache
   * só conhece o que este aparelho viu na última mutação: uma atividade que
   * outra pessoa criou depois não está nele (#84, #100).
   */
  doCache: boolean;
}

/**
 * @param data data civil `YYYY-MM-DD` vinda da rota `/roteiro/:dia`.
 */
export function useCarregarDia(data: string): EstadoDoDia {
  const { tripId, meuMemberId } = useTrip();
  const dias = useDiasDaViagem(tripId);
  const hydrate = useItinerary((s) => s.hydrate);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [doCache, setDoCache] = useState(false);

  const dia = dias.days?.find((d) => d.date === data);
  const dayId = dia?.id ?? null;

  useEffect(() => {
    if (!dayId) {
      // Sem lista ainda: seguir carregando. Lista chegou e o dia não existe:
      // não é erro de rede, é uma data fora da viagem.
      setCarregando(dias.carregando);
      setErro(dias.erro);
      return;
    }

    let vivo = true;
    setCarregando(true);
    setErro(false);
    setDoCache(false);

    void (async () => {
      const salvo = await loadItinerary(dayId).catch(() => null);
      if (!vivo) return;

      if (salvo) {
        hydrate(salvo);
        setDoCache(true);
        setCarregando(false);
        return;
      }

      try {
        const resposta = await lerDia(dayId);
        if (!vivo) return;

        hydrate({
          activities: atividadesDoServidor(resposta),
          history: [],
          currentMemberId: meuMemberId,
          reorderVersion: resposta.day.reorderVersion,
          dayId,
        });
        setCarregando(false);
      } catch {
        if (!vivo) return;
        // Sem rede e sem cache: o dia fica vazio e a tela diz que não deu.
        hydrate({
          activities: [],
          history: [],
          currentMemberId: meuMemberId,
          reorderVersion: 0,
          dayId,
        });
        setCarregando(false);
        setErro(true);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [dayId, meuMemberId, hydrate, dias.carregando, dias.erro]);

  return {
    dayId,
    titulo: dia?.title ?? null,
    carregando,
    erro,
    doCache,
  };
}
